/**
 * MergeModal — the merge tool (plan §7.5 "The merge tool re-points aliases,
 * budget lines, datapoints and studies to the survivor and marks the loser
 * merged_into"; §13.4 WP-33b frontend scope: "a merge control that asks for
 * confirmation naming both entries (ConfirmModal) and shows the re-pointing
 * counts and the datapoints gap afterwards"). Entitlement only (§4.2) — no
 * extra confirmation beyond this dialog.
 */
import { useState } from 'react';
import { Modal, ConfirmModal, FilterSelect, secondaryBtnCls, primaryBtnCls, useToast } from '../../../../ui';
import { mergeMasterEntries } from '../../../api/client';
import type { MasterEntry, MasterListType, MasterMergeResult } from '../../../api/client';

interface Props {
  isOpen: boolean;
  listType: MasterListType;
  loser: MasterEntry | null;
  /** Every other live entry of this list_type, to pick a survivor from. */
  candidates: MasterEntry[];
  onClose: () => void;
  onMerged: (result: MasterMergeResult) => void;
}

export function MergeModal({ isOpen, listType, loser, candidates, onClose, onMerged }: Props) {
  const { toast } = useToast();
  const [survivorId, setSurvivorId] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [merging, setMerging] = useState(false);
  const [result, setResult] = useState<MasterMergeResult | null>(null);

  const options = candidates.map((c) => ({ value: c.id, label: c.official_name }));
  const survivor = candidates.find((c) => c.id === survivorId) ?? null;

  function reset() {
    setSurvivorId('');
    setConfirming(false);
    setResult(null);
  }

  function handleClose() {
    reset();
    onClose();
  }

  async function handleConfirm() {
    if (!loser || !survivor) return;
    setMerging(true);
    try {
      const res = await mergeMasterEntries(listType, loser.id, survivor.id);
      setResult(res);
      setConfirming(false);
      toast(`${loser.official_name} merged into ${survivor.official_name}.`, { type: 'success' });
      onMerged(res);
    } catch {
      toast('Could not merge those entries.', { type: 'error' });
      setConfirming(false);
    } finally {
      setMerging(false);
    }
  }

  if (!loser) return null;

  return (
    <>
      <Modal isOpen={isOpen && !confirming} onClose={handleClose} title={`Merge — ${loser.official_name}`} maxWidth="max-w-md">
        <div className="space-y-4">
          {!result ? (
            <>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Merge <strong>{loser.official_name}</strong> into another entry of the same list. Everything that
                still points at it — aliases, budget lines, studies and datapoints — resolves to the survivor.
              </p>
              <div>
                <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Merge into</span>
                <FilterSelect
                  label="Merge into"
                  value={survivorId}
                  onChange={setSurvivorId}
                  options={[{ value: '', label: 'Choose a survivor entry…' }, ...options]}
                />
              </div>
              <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-4 dark:border-gray-700">
                <button type="button" onClick={handleClose} className={secondaryBtnCls}>
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={!survivor}
                  onClick={() => setConfirming(true)}
                  className={primaryBtnCls}
                  data-testid="merge-open-confirmation"
                >
                  Merge…
                </button>
              </div>
            </>
          ) : (
            <div className="space-y-3" data-testid="merge-result-summary">
              <p className="text-sm text-gray-700 dark:text-gray-200">
                <strong>{loser.official_name}</strong> is now merged into <strong>{survivor?.official_name}</strong>.
              </p>
              <dl className="grid grid-cols-2 gap-2 rounded-lg border border-gray-200 p-3 text-sm dark:border-gray-700">
                <dt className="text-gray-500 dark:text-gray-400">Aliases re-pointed</dt>
                <dd className="text-right font-medium text-gray-900 dark:text-gray-100">{result.aliases_repointed}</dd>
                <dt className="text-gray-500 dark:text-gray-400">Budget lines re-pointed</dt>
                <dd className="text-right font-medium text-gray-900 dark:text-gray-100">{result.budget_lines_repointed}</dd>
                <dt className="text-gray-500 dark:text-gray-400">Studies re-pointed</dt>
                <dd className="text-right font-medium text-gray-900 dark:text-gray-100">
                  {Object.values(result.studies_repointed ?? {}).reduce((a, b) => a + b, 0)}
                </dd>
                <dt className="text-gray-500 dark:text-gray-400">Datapoints pending</dt>
                <dd className="text-right font-medium text-gray-900 dark:text-gray-100">{result.datapoints_pending}</dd>
              </dl>
              {result.datapoints_gap && (
                <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
                  {result.datapoints_gap}
                </p>
              )}
              <div className="flex items-center justify-end border-t border-gray-100 pt-4 dark:border-gray-700">
                <button type="button" onClick={handleClose} className={primaryBtnCls}>
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      <ConfirmModal
        isOpen={isOpen && confirming}
        onClose={() => setConfirming(false)}
        onConfirm={handleConfirm}
        title="Merge these entries?"
        message={
          <>
            <strong>{loser.official_name}</strong> will be merged into <strong>{survivor?.official_name}</strong>.
            Its aliases, budget lines, studies and pending datapoints will resolve to the survivor; {loser.official_name}{' '}
            itself is kept, marked merged, never deleted.
          </>
        }
        confirmLabel="Merge"
        danger
        loading={merging}
      />
    </>
  );
}

export default MergeModal;
